import { ReactNode } from 'react';

import { Box } from '../Box';
import { Typography } from '../Typography';
import { Link } from '../Link';

/**
 * Свойства компонента {@link DialogContent}
 */
export interface DialogContentProps {
  /**
   * Идентификатор для автотестов.
   */
  testId?: string;

  /**
   * Функция, вызываемая при закрытии диалогового окна.
   */
  onClose: () => void;

  /**
   * Заголовок диалогового окна.
   */
  title?: string;

  /**
   * Дополнительный компонент, отображаемый рядом с заголовком.
   */
  titleChip?: ReactNode;

  /**
   * Флаг, определяющий, занимает ли диалоговое окно весь экран.
   */
  fullScreen?: boolean;

  /**
   * Компоненты действий, отображаемые в нижней части диалогового окна.
   */
  actions?: ReactNode;

  /**
   * Дочерние элементы, отображаемые в основном содержимом диалогового окна.
   */
  children?: ReactNode;

  /**
   * Убирает отступы внутри диалога, не затрагивая заголовок.
   *
   * @defaultValue `false`
   */
  withoutPadding?: boolean;
}

/**
 * Компонент DialogContent представляет содержимое диалогового окна.
 *
 * @public
 * @param onClose - Функция, вызываемая при закрытии диалогового окна.
 * @param title - Заголовок диалогового окна.
 * @param titleChip - Дополнительный компонент, отображаемый рядом с заголовком.
 * @param fullScreen - Флаг, определяющий, занимает ли диалоговое окно весь экран.
 * @param actions - Компоненты действий, отображаемые в нижней части диалогового окна.
 * @param children - Дочерние элементы, отображаемые в основном содержимом диалогового окна.
 * @param withoutPadding - Убирает отступы внутри диалога, не затрагивая заголовок.
 * @param testId - Тестовый идентификатор для компонента.
 * @returns  Содержимое диалогового окна.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link DialogContentProps}
 */
export const DialogContent = ({
  children,
  fullScreen,
  title,
  titleChip,
  actions,
  onClose,
  withoutPadding = false,
  testId,
}: DialogContentProps) => {
  const pySpace = fullScreen ? '32' : '48';
  const pxSpace = fullScreen ? '16' : '48';

  return (
    <Box display="flex" flexDirection="column" flexGrow="1" testId={testId}>
      <Box
        py={withoutPadding ? 'unset' : pySpace}
        px={withoutPadding ? 'unset' : pxSpace}
        flexGrow="1"
      >
        {title && (
          <Box
            display="flex"
            alignItems="flex-start"
            mb="24"
            flexDirection="column"
            px={withoutPadding ? pxSpace : 'unset'}
          >
            {fullScreen && (
              <Box mb="8">
                <Link size="small" onClick={onClose} stroke={false}>
                  Назад
                </Link>
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" width="100%">
              <Typography variant="h5">{title}</Typography>
              {titleChip}
            </Box>
          </Box>
        )}
        {children}
      </Box>
      {actions &&
        (fullScreen ? (
          <Box
            display="flex"
            justifyContent="center"
            gap="16"
            px={withoutPadding ? 'unset' : '16'}
            pb="24"
          >
            {actions}
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="flex-end"
            gap="16"
            p="16"
            bgcolor="bg.greyscale.primary"
          >
            {actions}
          </Box>
        ))}
    </Box>
  );
};
